// ABOUTME: The applications block at the top of the Jobs page: a quiet row when nothing moved today, open with an update
// ABOUTME: chip when something did, the rows inside one card. The toggle and the seen moves are kept for the session.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import { BoldChip } from 'components/BoldChip';
import { Text } from 'components/Text';
import { ToastPill } from 'components/toasts/ToastPill';
import { PATHS } from 'config/paths';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { Iconify } from 'modules/minimal-ui-theme';
import { useHideApplication } from '../hooks';
import { TalentApplication, TalentApplicationStatus } from '../types';
import {
  initialBlockOpen,
  rememberBlockOpen,
  rememberSeenMoves,
  seenMoves,
  unseenMoved
} from '../utils';
import { ApplicationCard } from './ApplicationCard';

interface Props {
  applications: TalentApplication[];
  /** The instant "today" is measured from; injectable so a test does not fake timers. */
  now: Date;
  /** PJL: rows can be hidden (PRO_APPLY_V2, decided by the caller); off, the block is today's. */
  canHide?: boolean;
  /** PJL: whether the feed's section comes open; without it the block decides from the moves. */
  feedOpen?: boolean;
}

/**
 * An application is an event stream about a job, and three of them sitting at "being reviewed" are worth no pixels.
 * So the block is one 44px row on a quiet day and opens by itself on the day something moved. The talent can toggle
 * it, and the toggle is kept for the session. Once the moved applications were seen open, the counter chip is put
 * away until a further move arrives. All of that lives in session storage on the device: a convenience, not a fact.
 */
export const ApplicationsBlock = ({
  applications,
  now,
  canHide = false,
  feedOpen
}: Props) => {
  // What was seen before this visit is fixed at mount; a move that arrives while the block is open still counts as new,
  // and so does a later move of an application whose earlier move was seen.
  const [seenAtMount] = useState(seenMoves);
  const unseen = unseenMoved(applications, now, seenAtMount);
  const inProgress = applications.filter(
    ({ status }) => status === TalentApplicationStatus.ACTIVE
  ).length;

  const [open, setOpen] = useState(() =>
    initialBlockOpen(unseen.length, feedOpen)
  );
  const showUpdates = unseen.length > 0;
  // PJL: the row hidden last, for the toast's undo. Only used when the caller allows hiding (PRO_APPLY_V2).
  const [undo, setUndo] = useState<string | null>(null);

  // What the talent saw open counts as seen when they leave, not while they look at it: the chip stays put for the
  // whole visit and is gone on the next one.
  const latest = useRef({ open, applications });
  latest.current = { open, applications };
  useEffect(
    () => () => {
      if (latest.current.open) {
        rememberSeenMoves(latest.current.applications, now);
      }
    },
    // `now` is the visit's clock; it does not change within a mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const toggle = () => {
    const next = !open;
    rememberBlockOpen(next);
    setOpen(next);
  };

  return (
    <Card
      data-testid="applications-block"
      sx={{ borderRadius: 4, boxShadow: 'none', mb: 2 }}
    >
      <ButtonBase
        data-testid="applications-block-toggle"
        onClick={toggle}
        aria-expanded={open}
        sx={{
          width: '100%',
          minHeight: 44,
          px: 2.5,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
          textAlign: 'left'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0
          }}
        >
          <Text
            variant="subtitle1"
            component="h2"
            color="text.primary"
            sx={{ fontWeight: 700 }}
          >
            {open || inProgress === 0
              ? getTrans('applications.block.title')
              : getTransWithParams('applications.block.quiet', {
                  count: inProgress
                })}
          </Text>
          {showUpdates && (
            <BoldChip
              variant="soft"
              size="small"
              color="primary"
              data-testid="applications-block-updates"
              label={getTransWithParams('applications.block.updates', {
                count: unseen.length
              })}
            />
          )}
          {!open && !showUpdates && inProgress > 0 && (
            <Text variant="caption" color="text.secondary">
              {getTrans('applications.block.noChange')}
            </Text>
          )}
        </Box>
        <Iconify
          icon="eva:arrow-ios-downward-fill"
          sx={{
            flexShrink: 0,
            transition: 'transform 150ms',
            transform: open ? 'rotate(180deg)' : 'none'
          }}
        />
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        {applications.map(application => (
          <Box
            key={application.refId}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              opacity: application.hidden ? 0.6 : 1
            }}
          >
            <Link
              to={`${PATHS.applications}/${application.refId}`}
              style={{ display: 'block', textDecoration: 'none' }}
            >
              <ApplicationCard
                application={application}
                framed={false}
                now={now}
              />
            </Link>
            {canHide && (
              <HideToggle
                application={application}
                onHidden={() => setUndo(application.refId)}
              />
            )}
          </Box>
        ))}
      </Collapse>
      {canHide && <UndoHideToast refId={undo} onDone={() => setUndo(null)} />}
    </Card>
  );
};

/**
 * PJL: the row's own control to hide it from the talent's list, or to show a hidden one again. Next to the row's link,
 * not inside it, so the two are separate targets. Hiding changes nothing for Zenjob or the company.
 */
const HideToggle = ({
  application,
  onHidden
}: {
  application: TalentApplication;
  onHidden: () => void;
}) => {
  const { setHidden } = useHideApplication();
  return (
    <ButtonBase
      data-testid={application.hidden ? 'application-show' : 'application-hide'}
      onClick={() => {
        const next = !application.hidden;
        void setHidden(application.refId, next)
          .then(() => {
            if (next) onHidden();
          })
          .catch(() => undefined);
      }}
      sx={{
        position: 'absolute',
        right: 12,
        bottom: 12,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        fontSize: 12,
        fontWeight: 600,
        color: 'text.secondary'
      }}
    >
      {getTrans(
        application.hidden
          ? 'applications.hide.show'
          : 'applications.hide.action'
      )}
    </ButtonBase>
  );
};

/** „Bewerbung ausgeblendet · Rückgängig“ for five seconds after a row was hidden. */
const UndoHideToast = ({
  refId,
  onDone
}: {
  refId: string | null;
  onDone: () => void;
}) => {
  const { setHidden } = useHideApplication();

  useEffect(() => {
    if (!refId) return undefined;
    const timeout = setTimeout(onDone, 5000);
    return () => clearTimeout(timeout);
  }, [refId, onDone]);

  return (
    <ToastPill
      open={!!refId}
      message={getTrans('applications.hide.done')}
      action={{
        label: getTrans('applications.hide.undo'),
        onClick: () => {
          onDone();
          if (refId) void setHidden(refId, false).catch(() => undefined);
        }
      }}
    />
  );
};
